import type { ButtonHTMLAttributes, ReactNode } from "react";
import { useLanguage } from "../i18n/LanguageProvider";

type ButtonVariant = "primary" | "secondary" | "danger";

export function Button({
  variant = "secondary",
  loading = false,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; loading?: boolean }) {
  return (
    <button
      className={`btn btn--${variant}`}
      aria-busy={loading || undefined}
      disabled={rest.disabled || loading}
      {...rest}
    >
      {loading ? "…" : children}
    </button>
  );
}

export function Panel({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="panel" aria-label={title}>
      {title && <p className="panel__title">{title}</p>}
      {children}
    </section>
  );
}

export function SkipLink() {
  const { t } = useLanguage();
  return (
    <a className="skip-link" href="#main-content">
      {t("skipToMain")}
    </a>
  );
}

type StateKind = "loading" | "empty" | "error";

export function StatePanel({
  kind,
  message,
  onRetry,
  children,
}: {
  kind: StateKind;
  message?: string;
  onRetry?: () => void;
  children?: ReactNode;
}) {
  const { t } = useLanguage();
  if (kind === "loading") {
    return (
      <div className="state-panel" role="status" aria-live="polite">
        <div className="skeleton" style={{ height: 96 }} />
        <span className="visually-hidden">{t("loading")}</span>
      </div>
    );
  }
  return (
    <div className="state-panel" role={kind === "error" ? "alert" : undefined}>
      <p>{message ?? (kind === "error" ? t("somethingWrong") : "")}</p>
      {children}
      {kind === "error" && onRetry && (
        <Button variant="secondary" onClick={onRetry}>
          {t("retry")}
        </Button>
      )}
    </div>
  );
}

export function Toast({ message, assertive = false }: { message: string; assertive?: boolean }) {
  return (
    <div className="toast" role="status" aria-live={assertive ? "assertive" : "polite"}>
      {message}
    </div>
  );
}
